import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import type { FC } from 'react';

import { cn } from '@/lib/utils';

import { PERSONA_LABELS, FOOTER_ROWS, SIGN_OUT_ROW } from 'constants/navigation';

import { Avatar } from '../../ui/Avatar';
import { NavItem } from '../../ui/NavItem';
import { Wordmark } from '../../ui/Wordmark';
import { SidebarSection } from '../SidebarSection';

import type { TSidebarProps } from './Sidebar.props';

const STORAGE_KEY = 'the500-sidebar-collapsed';
const WORDMARK_WIDTH = 100;

const useCollapsed = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(window.localStorage.getItem(STORAGE_KEY) === 'true');
  }, []);

  const toggle = () => {
    setIsCollapsed((previous) => {
      const next = !previous;

      window.localStorage.setItem(STORAGE_KEY, String(next));

      return next;
    });
  };

  return { isCollapsed, toggle };
};

const Sidebar: FC<TSidebarProps> = ({
  persona, name, sections, className, onSignOut,
}) => {
  const { isCollapsed, toggle } = useCollapsed();
  const { pathname } = useLocation();

  return (
    <aside
      className={cn(
        'bg-surface-subtle border-border flex h-full shrink-0 flex-col border-r transition-[width]',
        isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        className,
      )}
    >
      <div className={cn('pt-6', isCollapsed ? 'px-3' : 'px-5')}>
        {isCollapsed ? null : <Wordmark width={WORDMARK_WIDTH} />}
        <div className={cn('flex items-center gap-2.5', isCollapsed ? 'justify-center' : 'mt-5.5')}>
          <Avatar tone="accent" size="36" name={name} />
          {isCollapsed ? null : (
            <span className="flex min-w-0 flex-col">
              <span className="text-control-semibold text-text-primary truncate">{name}</span>
              <span className="text-caption-body text-text-muted truncate">{PERSONA_LABELS[persona]}</span>
            </span>
          )}
        </div>
      </div>

      <nav className={cn('flex-1 overflow-y-auto', isCollapsed ? 'p-2' : 'p-3')}>
        {sections.map(section => (
          <SidebarSection
            key={section.eyebrow ?? 'home'}
            section={section}
            pathname={pathname}
            isCollapsed={isCollapsed}
          />
        ))}
      </nav>

      <div className={cn('border-border flex flex-col gap-0.5 border-t', isCollapsed ? 'p-2' : 'p-3')}>
        {FOOTER_ROWS.map(row => (
          <NavItem
            key={row.label}
            label={row.label}
            icon={row.icon}
            href={row.href}
            isActive={row.href === pathname}
            isCollapsed={isCollapsed}
          />
        ))}
        <NavItem
          label={SIGN_OUT_ROW.label}
          icon={SIGN_OUT_ROW.icon}
          isCollapsed={isCollapsed}
          onClick={onSignOut}
        />
        <NavItem
          label={isCollapsed ? 'Expand' : 'Collapse'}
          icon="panel-left-close"
          isCollapsed={isCollapsed}
          onClick={toggle}
        />
      </div>
    </aside>
  );
};

export {
  Sidebar,
};
